// @ts-ignore
import { LOCAL_STORAGE_PREFIX } from "app/_helpers/app.constants";
import * as oauth2 from "oauth4webapi";

export enum LocalStorageKey {
  account = "account",
  token = "token",
  isLegacyLogin = "isLegacyLogin",
  authorisationServer = "authorisationServer",
  client = "client",
  redirectUri = "redirectUri",
  codeVerifier = "codeVerifier",
  accessToken = "accessToken",
  refreshToken = "refreshToken",
  idToken = "idToken",
  isUsingAlternateApiLoginUrl = "isUsingAlternateApiLoginUrl",
  countries = "countries",
  pointerType = "pointerType",
}
export enum NgLocalStorageKey {
  isManager = "isManager",
  account = "account",
  venue = "venue",
  couchUsers = "couchUsers",
  event = "event",
  token = "token",
}
export interface AuthUser {
  id: string;
  name: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  accountId?: string;
  roles?: string[];
}
const NS = "OAuth2Service";
const LOCAL_STORAGE_REFRESH_VERSION = "localStorageRefreshVersion";
const OAUTH_TOKENS = "oauth_tokens";
const POPUP_WINDOW_CONFIGURE = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=400,height=550,left=-1000,top=-1000`;
const SYNCED_LOGOUT = "syncedLogout";
class OAuth2Service {
  private authSettings: any;
  private localStorageGet: any;
  private localStorageSet: any;
  private localStorageRemove: any;
  private isEnableOAuth = true;
  private timeoutId: any;
  private refreshingAccessToken: boolean = false;
  private refreshVersion: number = 0;
  private localStoragePrefix: string;
  init(
    oauth2: any,
    localStorageGet: any,
    localStorageSet: any,
    localStorageRemove: any,
    localStoragePrefix: string
  ) {
    /**
     * From /config/{env}.config html injection
     * Checks to ensure data exists for the current environment.
     */

    if (!oauth2) {
      console.error(
        NS,
        `Could not find "oauth2" object. Check webpack.config.js and ensure it is added for your environment.`
      );
      return;
    }

    const mandatoryProps = [
      "authority",
      "clientId",
      "logout_uri",
      "redirect_uri",
      "response_type",
      "clientRoot",
      "clientScope",
    ];
    if (mandatoryProps.some((propName) => !oauth2[propName])) {
      console.warn(
        NS,
        `Check "oauth2" object and ensure all properties from list exist`,
        mandatoryProps
      );
    }

    this.authSettings = oauth2;
    this.localStorageGet = localStorageGet;
    this.localStorageSet = localStorageSet;
    this.localStorageRemove = localStorageRemove;
    this.isEnableOAuth = !this.localStorageGet(LocalStorageKey.isLegacyLogin);
    this.localStoragePrefix = localStoragePrefix;
    const localStorageRefreshVersion = this.localStorageGet(
      LOCAL_STORAGE_REFRESH_VERSION
    );
    if (localStorageRefreshVersion) {
      this.refreshVersion = parseInt(localStorageRefreshVersion, 10);
    } else {
      this.localStorageSet(LOCAL_STORAGE_REFRESH_VERSION, this.refreshVersion);
    }
    window.addEventListener("storage", (event) => {
      if (
        event.key === this.localStoragePrefix + LocalStorageKey.idToken &&
        event.newValue !== event.oldValue
      ) {
        try {
          const newValue = JSON.parse(event.newValue);
          if (!newValue) {
            window.location.href = this.authSettings.clientRoot;
          }
        } catch (e) {
          // console.log('error parsing value, ignore please', e);
        }
      }
    });
  }
  // this method for handle multiple tabs login
  isRefreshVersionSame() {
    const localStorageRefreshVersion = this.localStorageGet(
      LOCAL_STORAGE_REFRESH_VERSION
    );
    if (localStorageRefreshVersion) {
      const refreshVersion = parseInt(localStorageRefreshVersion, 10);
      if (refreshVersion > this.refreshVersion) {
        this.refreshVersion = refreshVersion;
        return false;
      }
      return true;
    } else {
      this.localStorageSet(LOCAL_STORAGE_REFRESH_VERSION, this.refreshVersion);
      return true;
    }
  }
  setIsEnableOAuth(isEnableOAuth: boolean) {
    this.isEnableOAuth = isEnableOAuth;
    this.localStorageSet(LocalStorageKey.isLegacyLogin, !isEnableOAuth);
  }
  getIsEnableOAuth() {
    return this.isEnableOAuth;
  }
  async callOAuth(state?: string): Promise<string> {
    this.removeStoredDataForOauth();
    const issuer = new URL(this.authSettings.authority);
    const as = await oauth2
      .discoveryRequest(issuer)
      .then((response: any) =>
        oauth2.processDiscoveryResponse(issuer, response)
      );

    const client: oauth2.Client = {
      client_id: this.authSettings.clientId,
      token_endpoint_auth_method: "none",
    };

    if (as.code_challenge_methods_supported?.includes("S256") !== true) {
      // This example assumes S256 PKCE support is signalled
      // If it isn't supported, random `nonce` must be used for CSRF protection.
      throw new Error();
    }

    const code_verifier = oauth2.generateRandomCodeVerifier();
    const code_challenge = await oauth2.calculatePKCECodeChallenge(
      code_verifier
    );
    const code_challenge_method = "S256";

    // redirect user to as.authorization_endpoint`

    const authorizationUrl = new URL(as.authorization_endpoint);
    authorizationUrl.searchParams.set("client_id", client.client_id);
    authorizationUrl.searchParams.set("code_challenge", code_challenge);
    authorizationUrl.searchParams.set(
      "code_challenge_method",
      code_challenge_method
    );
    authorizationUrl.searchParams.set(
      "redirect_uri",
      this.authSettings.redirect_uri
    );
    authorizationUrl.searchParams.set("response_type", "code");
    authorizationUrl.searchParams.set("scope", this.authSettings.clientScope);
    if (state) {
      authorizationUrl.searchParams.set("state", state);
    }

    this.localStorageSet(
      LocalStorageKey.authorisationServer,
      JSON.stringify(as)
    );
    this.localStorageSet(LocalStorageKey.client, JSON.stringify(client));
    this.localStorageSet(LocalStorageKey.codeVerifier, code_verifier);
    return authorizationUrl.href;
  }
  async login(usePopUp = true, state?: string) {
    let newWindow: Window;
    if (usePopUp) {
      newWindow = window.open("", "login-window", POPUP_WINDOW_CONFIGURE);
    }
    this.callOAuth(state).then((url: string) => {
      // pop up a new window for login
      if (usePopUp && newWindow) {
        newWindow.location.href = url;
      } else {
        // open in same window
        window.location.href = url;
      }
    });
  }
  async getOAuthToken() {
    const authorisationServer = JSON.parse(
      this.localStorageGet(LocalStorageKey.authorisationServer) || "{}"
    );
    const client = JSON.parse(
      this.localStorageGet(LocalStorageKey.client) || "{}"
    );
    const codeVerifier =
      this.localStorageGet(LocalStorageKey.codeVerifier) || "";

    const currentUrl: URL = new URL(window.location.href);
    // follow line for the error of unexpected parameter.
    currentUrl.searchParams.delete("state");
    const params = oauth2.validateAuthResponse(
      authorisationServer,
      client,
      currentUrl,
      oauth2.expectNoState
    );
    if (oauth2.isOAuth2Error(params)) {
      console.log("error", params);
      throw new Error(); // Handle OAuth 2.0 redirect error
    }

    const response = await oauth2.authorizationCodeGrantRequest(
      authorisationServer,
      client,
      params,
      this.authSettings.redirect_uri,
      codeVerifier
    );

    let challenges: oauth2.WWWAuthenticateChallenge[] | undefined;
    if ((challenges = oauth2.parseWwwAuthenticateChallenges(response))) {
      for (const challenge of challenges) {
        console.log("challenge", challenge);
      }
      throw new Error(); // Handle www-authenticate challenges as needed
    }

    const result = await oauth2.processAuthorizationCodeOpenIDResponse(
      authorisationServer,
      client,
      response
    );
    if (oauth2.isOAuth2Error(result)) {
      throw new Error(result.error); // Handle OAuth 2.0 response body error
    }

    this.setTokenToStorage(result);
    return result.access_token;
  }

  async refreshAccessToken(): Promise<string> {
    // for the case when the token is being refreshed, wait for the token to be refreshed
    if (this.refreshingAccessToken) {
      return new Promise((resolve, reject) => {
        let count = 0;
        const maxAttempts = 3;
        const intervalId = setInterval(() => {
          count++;
          if (!this.refreshingAccessToken) {
            clearInterval(intervalId);
            resolve(this.getOAuth2AccessToken());
          } else if (count >= maxAttempts) {
            clearInterval(intervalId);
            this.refreshingAccessToken = false;
            resolve(this.refreshAccessToken());
          }
        }, 800);
      });
    }
    // for the case that other tab refreshed token already
    if (!this.isRefreshVersionSame()) {
      return Promise.resolve(this.getOAuth2AccessToken());
    }
    this.refreshingAccessToken = true;
    setTimeout(() => {
      this.refreshingAccessToken = false;
    }, 8000);
    let result: oauth2.TokenEndpointResponse | oauth2.OAuth2Error;
    let client: oauth2.Client;
    let authorisationServer;
    try {
      const authorisationServerString = this.localStorageGet(
        LocalStorageKey.authorisationServer
      );
      const clientString = this.localStorageGet(LocalStorageKey.client);
      if (!authorisationServerString || !clientString) {
        const issuer = new URL(this.authSettings.authority);
        authorisationServer = await oauth2
          .discoveryRequest(issuer)
          .then((response: any) =>
            oauth2.processDiscoveryResponse(issuer, response)
          );
        client = {
          client_id: this.authSettings.clientId,
          token_endpoint_auth_method: "none",
        };
      } else {
        authorisationServer = JSON.parse(authorisationServerString);
        client = JSON.parse(clientString);
      }
      const refreshToken = this.localStorageGet(LocalStorageKey.refreshToken);
      const response = await oauth2.refreshTokenGrantRequest(
        authorisationServer,
        client,
        refreshToken
      );
      result = await oauth2.processRefreshTokenResponse(
        authorisationServer,
        client,
        response
      );
      if (oauth2.isOAuth2Error(result)) {
        // if refresh token has expired
        if (response.status === 400 && result.error === "invalid_grant") {
          let token = "";
          try {
            // get AccessToken and refresh token again
            token = await this.getOAuthToken();
          } catch (tokenError) {
            console.log("tokenError - need to get a new token", tokenError);
            const sessionExpiredURL = new URL(this.authSettings.clientRoot);
            sessionExpiredURL.searchParams.set("session_expired", "true");
            this.refreshingAccessToken = false;
            this.logout().finally(() => {
              this.removeStoredDataForOauth();
              window.location.replace(sessionExpiredURL.href);
            });
            // window.alert('warning, error')
            return "";
          }
          this.refreshingAccessToken = false;
          return Promise.resolve(token as string);
        }
        this.refreshingAccessToken = false;
        throw new Error(); // Handle OAuth 2.0 response body error
      }
    } catch (error) {
      console.log("error refreshing token", error);
      this.refreshingAccessToken = false;
      throw new Error();
    } finally {
      this.refreshingAccessToken = false;
    }

    if (result) {
      this.refreshVersion += 1;
      this.localStorageSet(LOCAL_STORAGE_REFRESH_VERSION, this.refreshVersion);
    }
    this.setTokenToStorage(result);
    this.refreshingAccessToken = false;
    return Promise.resolve(result.access_token as string);
  }
  parseState(state?: string) {
    if (!state) {
      return {};
    }
    const stateParams = state.split("&");
    const stateObj: any = {};
    stateParams.forEach((param) => {
      const paramArr = param.split("=");
      stateObj[paramArr[0]] = paramArr[1];
    });
    return stateObj;
  }
  setTokenToStorage(result: oauth2.TokenEndpointResponse) {
    this.storeTokens(result);
    this.setOAuth2AccessToken(result.access_token);
    this.setOAuth2RefreshToken(result.refresh_token || "");
    this.setOAuth2IdToken(result.id_token || "");
  }
  extractUserInfo(token: string): AuthUser | null {
    try {
      const payload = this.parseJwt(token);
      if (!payload) return null;

      return {
        id: payload.idp_sub,
        name: payload.name || payload.preferred_username || "Unknown User",
        email: payload.email,
        username: payload.preferred_username,
        firstName: payload.given_name,
        lastName: payload.family_name,
        roles: payload.role,
        accountId: payload.idp_account_id,
      };
    } catch (error) {
      console.error("Error extracting user info from token:", error);
      return null;
    }
  }
  storeTokens(tokenResponse: oauth2.TokenEndpointResponse) {
    const expiresAt = Date.now() + tokenResponse.expires_in * 1000;
    const user = this.extractUserInfo(tokenResponse.access_token);

    const storedTokens = {
      accessToken: tokenResponse.access_token,
      idToken: tokenResponse.id_token,
      refreshToken: tokenResponse.refresh_token,
      expiresAt,
      tokenType: tokenResponse.token_type,
      user: user || undefined,
    };

    localStorage.setItem(OAUTH_TOKENS, JSON.stringify(storedTokens));
  }
  getStoredTokens() {
    const storedTokens = localStorage.getItem(OAUTH_TOKENS);
    if (!storedTokens) {
      return null;
    }
    return JSON.parse(storedTokens);
  }
  getOAuth2AccessToken() {
    const storedTokens = this.getStoredTokens();
    if (storedTokens) {
      return storedTokens.accessToken;
    }
    return this.localStorageGet(LocalStorageKey.accessToken);
  }
  // this function is only for requestHeader.ts, the load priority make it cannot use the getOAuth2AccessToken()
  // as some service use request headers, so need to get the token from local storage directly.
  getOAuth2AccessTokenForHeader() {
    const storedTokens = this.getStoredTokens();
    if (storedTokens) {
      return storedTokens.accessToken;
    }

    return JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_PREFIX + LocalStorageKey.accessToken)
    );
  }
  setOAuth2AccessToken(accessToken: string) {
    return this.localStorageSet(LocalStorageKey.accessToken, accessToken);
  }
  getOAuth2RefreshToken() {
    const storedTokens = this.getStoredTokens();
    if (storedTokens) {
      return storedTokens.refreshToken;
    }
    return this.localStorageGet(LocalStorageKey.refreshToken);
  }
  setOAuth2RefreshToken(refreshToken: string) {
    return this.localStorageSet(
      LocalStorageKey.refreshToken,
      refreshToken || ""
    );
  }
  getOAuth2IdToken() {
    const storedTokens = this.getStoredTokens();
    if (storedTokens) {
      return storedTokens.idToken;
    }
    return this.localStorageGet(LocalStorageKey.idToken);
  }
  setOAuth2IdToken(idToken: string) {
    return this.localStorageSet(LocalStorageKey.idToken, idToken);
  }

  getBearerToken(): string {
    return "Bearer " + this.getOAuth2AccessToken();
  }

  deleteCookies() {
    const allCookies = document.cookie.split(";");
    for (let i = 0; i < allCookies.length; i++) {
      document.cookie =
        allCookies[i] + "=;expires=" + new Date(0).toUTCString();
    }
  }
  async logout() {
    const logoutIdToken = this.getOAuth2IdToken();
    if (logoutIdToken) {
      const iamLogoutURL = new URL(
        `${this.authSettings.authority}${this.authSettings.logout_uri}`
      );
      iamLogoutURL.searchParams.set("id_token_hint", logoutIdToken);
      iamLogoutURL.searchParams.set("client_id", this.authSettings.clientId);
      await fetch(iamLogoutURL.toString());
    }
  }

  /***
   * This method is for refreshing the token in the background
   * @param accessToken - if not passed, it will take the token from local storage
   * @param newTokenCallback - callback function to be called when the new token is received, will be called with the new token as parameter
   * @param errorCallBack - callback function to be called when there is an error, will be called with the error as parameter
   * @param secondsBeforeTokenExpires - seconds before the token expires, default is 30 seconds
   */
  periodicalCheckForTokenValidity(
    accessToken: string = null,
    newTokenCallback: (token: string) => void = null,
    errorCallBack: any = null,
    secondsBeforeTokenExpires: number = 30
  ) {
    const token = accessToken || this.getOAuth2AccessToken();
    const accessTokenClaims = this.parseJwt(token);
    const now = Date.now() / 1000;
    const randomSeconds = Math.floor(Math.random() * 60) + 1; // random seconds between 1 and 60
    // console.log('randomSeconds-----------------------', randomSeconds);
    let count = accessTokenClaims.exp - now - secondsBeforeTokenExpires;
    // let count = secondsBeforeTokenExpires + 40;
    // if token is expired, return false
    if (count <= 0) {
      const sessionExpiredURL = new URL(this.authSettings.clientRoot);
      sessionExpiredURL.searchParams.set("session_expired", "true");
      this.refreshingAccessToken = false;
      this.logout().finally(() => {
        this.removeStoredDataForOauth();
        window.location.replace(sessionExpiredURL.href);
      });
      return false;
    }
    count -= randomSeconds;
    // console.log('count-----------------------', count);
    // if already a timeout is set, clear it
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      this.refreshAccessToken()
        .then((newToken) => {
          if (newTokenCallback) {
            newTokenCallback(newToken);
          }
          this.setOAuth2AccessToken(newToken);
          this.periodicalCheckForTokenValidity(newToken, newTokenCallback);
        })
        .catch((error) => {
          console.log("error refreshing token", error);
          if (errorCallBack) {
            errorCallBack(error);
          }
        });
    }, count * 1000);
    return true;
  }
  checkForTokenValidity(accessToken: any) {
    if (!accessToken) {
      return false;
    }
    const accessTokenClaims = this.parseJwt(accessToken);
    const isTokenValid = this.tokenValid(accessTokenClaims);
    return isTokenValid;
  }
  async checkForUserAuth() {
    const accessToken = this.getOAuth2AccessToken();
    if (!accessToken) {
      return false;
    }
    const accessTokenClaims = this.parseJwt(accessToken);
    const isTokenValid = this.tokenValid(accessTokenClaims);
    if (isTokenValid) {
      return true;
    }
    const refreshToken = this.getOAuth2RefreshToken();
    if (!refreshToken) {
      return false;
    }
    const refreshTokenClaims = this.parseJwt(refreshToken);
    const isRefreshTokenValid = this.tokenValid(refreshTokenClaims);
    if (!isRefreshTokenValid) {
      return false;
    } else {
      try {
        const newAccessToken = await this.refreshAccessToken();
        const newAccessTokenClaims = this.parseJwt(newAccessToken);
        return this.tokenValid(newAccessTokenClaims);
      } catch (e) {
        return false;
      }
    }
  }
  // this method for now only works for the sync way, which OAuth2 not enabled.
  checkForUserAuthSync() {
    const accessToken = this.getOAuth2AccessToken();
    if (!accessToken) {
      return false;
    }
    const accessTokenClaims = this.parseJwt(accessToken);
    const isTokenValid = this.tokenValid(accessTokenClaims);
    if (isTokenValid) {
      return true;
    }
    const refreshToken = this.getOAuth2RefreshToken();
    if (!refreshToken) {
      return false;
    }
    const refreshTokenClaims = this.parseJwt(refreshToken);
    const isRefreshTokenValid = this.tokenValid(refreshTokenClaims);
    if (!isRefreshTokenValid) {
      return false;
    } else {
      try {
        this.refreshAccessToken();
        return true;
      } catch (e) {
        return false;
      }
    }
  }
  removeStoredDataForOauth() {
    this.localStorageRemove(LocalStorageKey.accessToken);
    this.localStorageRemove(LocalStorageKey.authorisationServer);
    this.localStorageRemove(LocalStorageKey.client);
    this.localStorageRemove(LocalStorageKey.codeVerifier);
    this.localStorageRemove(LocalStorageKey.refreshToken);
    this.localStorageRemove(LocalStorageKey.idToken);
    this.localStorageRemove(NgLocalStorageKey.account);
    this.localStorageRemove(NgLocalStorageKey.venue);
    this.localStorageRemove(NgLocalStorageKey.isManager);
    this.localStorageRemove(NgLocalStorageKey.couchUsers);
    this.localStorageRemove(LocalStorageKey.isLegacyLogin);
    this.localStorageRemove(SYNCED_LOGOUT);
    this.localStorageRemove(LOCAL_STORAGE_REFRESH_VERSION);
    localStorage.removeItem(OAUTH_TOKENS);
    this.deleteCookies();
  }

  parseJwt(token: any) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  }

  tokenValid(claims: any) {
    const now = Date.now() / 1000;
    return now < claims.exp;
  }
}

const instance = new OAuth2Service();
export default instance as OAuth2Service;

// @ts-ignore
window["OAuth2Service"] = instance;
